import React, { useState, useEffect } from 'react';
import WalkthroughTopBar from './WalkthroughTopBar';
import DesignCanvas from '../../../organisms/design-canvas/DesignCanvas';
import WalkthroughCanvasToolbar from './WalkthroughCanvasToolbar';
import WalkthroughAddElementModal from './WalkthroughAddElementModal';
import WalkthroughScreenshotModal from './WalkthroughScreenshotModal';
import WalkthroughFilmstrip from './WalkthroughFilmstrip';
import WalkthroughInspector from './WalkthroughInspector';
import WalkthroughLivePreview from './WalkthroughLivePreview';
import './WalkthroughEditorV2.scss';

// Walkthrough Editor v2 — shell (Phase 0 authoring redesign).
//
// Mirrors the Path Editor v2 pattern: a parallel redesign that reuses the
// existing Konva `DesignCanvas` and Redux state, switched on by the USE_V2
// flag in ../Editor.js. Composes the redesigned frame:
//   • BL-138 — bottom step filmstrip (replaces the left StepSidebar)
//   • BL-139 — collapsible contextual inspector (replaces EditorOptions)
//   • BL-140 — canvas toolbar (undo/redo, zoom, replace screenshot)
//   • BL-141 — Add Interactive Element library modal
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2;
const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

const WalkthroughEditorShell = ({ id, companyName, materialName, initialPreview }) => {
  const [zoom, setZoom] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [filmOpen, setFilmOpen] = useState(true);
  const [inspOpen, setInspOpen] = useState(true);
  const [previewMode, setPreviewMode] = useState(!!initialPreview);

  // The step empty-state's "Choose from library" (in the shared EmptyCanvas)
  // opens the same screenshot library modal via a window event (BL-145).
  useEffect(() => {
    const open = () => setLibOpen(true);
    window.addEventListener('un-we-open-screenshot-library', open);
    return () => window.removeEventListener('un-we-open-screenshot-library', open);
  }, []);

  return (
    <div className="un-we-root">
      <WalkthroughTopBar
        id={id}
        companyName={companyName}
        materialName={materialName}
        previewMode={previewMode}
        onTogglePreview={() => setPreviewMode((v) => !v)}
      />

      <div className="un-we-body">
        <div className="un-we-canvas-col">
          <main className="un-we-canvas">
            {!previewMode && (
              <WalkthroughCanvasToolbar
                zoom={zoom}
                onZoomIn={() => setZoom((z) => clampZoom(z + 0.1))}
                onZoomOut={() => setZoom((z) => clampZoom(z - 0.1))}
                onZoomReset={() => setZoom(1)}
                onAddElement={() => setAddOpen(true)}
                onReplaceScreenshot={() => setLibOpen(true)}
                filmOpen={filmOpen}
                inspOpen={inspOpen}
                onToggleFilm={() => setFilmOpen((v) => !v)}
                onToggleInsp={() => setInspOpen((v) => !v)}
              />
            )}

            <div className={`un-we-canvas__scroll${previewMode ? ' is-preview' : ''}`}>
              {previewMode ? (
                <WalkthroughLivePreview />
              ) : (
                /* Browser-chrome frame around the Konva stage (presentational). */
                <div className="un-we-frame" style={{ transform: `scale(${zoom})` }}>
                  <div className="un-we-frame__bar">
                    <span className="un-we-frame__dots"><i /><i /><i /></span>
                    <span className="un-we-frame__url">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                      app.example.com
                    </span>
                  </div>
                  <div className="un-we-frame__view">
                    <DesignCanvas />
                  </div>
                </div>
              )}
            </div>
          </main>

          {/* BL-138 — bottom step filmstrip (toggleable, hidden in preview) */}
          {filmOpen && !previewMode && <WalkthroughFilmstrip />}
        </div>

        {/* BL-139 — collapsible contextual inspector. In Preview it stays open
            and pinned to the Appearance controls so theme edits are live. */}
        {(inspOpen || previewMode) && (
          <WalkthroughInspector appearanceOnly={previewMode} onClose={() => setInspOpen(false)} />
        )}
      </div>

      {/* BL-141 — Add Interactive Element library */}
      <WalkthroughAddElementModal open={addOpen} onClose={() => setAddOpen(false)} />

      {/* BL-142 — cross-walkthrough Screenshot Library */}
      <WalkthroughScreenshotModal open={libOpen} onClose={() => setLibOpen(false)} />
    </div>
  );
};

export default WalkthroughEditorShell;
